import React, { useEffect, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { Button, LinkButton, StandardButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Icon, IconTypes } from '@eika/icon';
import { Link, LinkType } from '@eika/link';
import { TrashcanAnimation } from '@eika/trashcan-animation';

import ActionResultInfo from '../../components/shared/ActionResultInfo/ActionResultInfo';
import { Content } from '../../components/shared/Content';
import { SelectList } from '../../components/shared/SelectList';
import FieldSkeleton from '../../components/transaction/FieldSkeleton';
import { useConfig } from '../../defaultConfig';
import useSalaryInfo, { useMutateSalaryInfo } from '../../fetchHooks/useSalaryInfo';
import type { AccountNumber } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import SalaryAccountField from './components/SalaryAccountField';
import SalaryDateField from './components/SalaryDateField/SalaryDateField';

import './EditSalaryInfo.scss';

const EditSalaryInfo: React.FC = () => {
    const translated = useTranslation();
    const { frontPageLink } = useConfig();
    const [showDeletionDialog, setShowDeletionDialog] = useState(false);
    const [paydays, setPaydays] = useState<Record<number, boolean>>();
    const [paydaysError, setPaydaysError] = useState<string | undefined>();
    const [selectedAccount, setSelectedAccount] = useState<AccountNumber>();
    const [accountError, setAccountError] = useState<string | undefined>();
    const { data: salaryInfo, isLoading } = useSalaryInfo();
    const { save, remove } = useMutateSalaryInfo();

    useEffect(() => {
        setSelectedAccount(salaryInfo?.accountNumber);
        const updatedPaydays = salaryInfo?.paydays.reduce(
            (record, day) => ({
                ...record,
                [day]: true,
            }),
            { 32: !!salaryInfo?.lastDayOfMonth },
        );
        setPaydays(updatedPaydays);
    }, [salaryInfo]);

    useEffect(() => {
        setPaydaysError(undefined);
        setAccountError(undefined);
    }, [paydays, selectedAccount]);

    const validate = (): boolean => {
        const missingPaydays = !Object.values(paydays ?? {}).some((v) => v);
        if (missingPaydays) {
            setPaydaysError(translated.salaryInfo.edit.paydaysError);
        }
        if (!selectedAccount) {
            setAccountError(translated.salaryInfo.edit.accountError);
        }
        return !missingPaydays && !!selectedAccount;
    };

    const selectedDays = Object.keys(paydays ?? {})
        .map((day) => parseInt(day, 10))
        .filter((day) => day < 32 && !!paydays?.[day]);

    const saveSalaryInfo = () => {
        save.mutate({
            accountNumber: selectedAccount!,
            paydays: selectedDays,
            lastDayOfMonth: !!paydays?.[32],
        });
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validate()) return;
        saveSalaryInfo();
    };

    const deleteSalaryInfo = () => {
        setAccountError(undefined);
        setPaydaysError(undefined);
        remove.mutate();
    };

    const hasChange =
        selectedAccount?.value !== salaryInfo?.accountNumber.value ||
        paydays?.[32] !== salaryInfo?.lastDayOfMonth ||
        !salaryInfo?.paydays.every((day) => selectedDays.some((selectedDay) => selectedDay === day)) ||
        salaryInfo?.paydays.length !== selectedDays.length;

    const frontPageButton = () => {
        if (frontPageLink) {
            return (
                <Link
                    className="edit-salary-info--result__btn"
                    linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                    href={frontPageLink?.url}
                >
                    {translated.salaryInfo.edit.toFrontpageLink}
                </Link>
            );
        }
        return <></>;
    };

    if (save.isSuccess || save.error) {
        return (
            <div className="edit-salary-info--result">
                <ActionResultInfo
                    title={
                        salaryInfo ? translated.salaryInfo.edit.updateSuccess : translated.salaryInfo.edit.saveSuccess
                    }
                    retry={deleteSalaryInfo}
                    defaultError={
                        salaryInfo ? translated.salaryInfo.edit.updateError : translated.salaryInfo.edit.saveError
                    }
                    error={save.error as RequestExceptionWithResponse}
                    icon={<CheckmarkAnimation className="edit-salary-info--result__icon" />}
                    successButton={frontPageButton()}
                />
            </div>
        );
    }

    if (remove.isSuccess || remove.error) {
        return (
            <div className="edit-salary-info--result">
                <ActionResultInfo
                    title={translated.salaryInfo.edit.deleteSuccess}
                    retry={deleteSalaryInfo}
                    defaultError={translated.salaryInfo.edit.deleteError}
                    error={remove.error as RequestExceptionWithResponse}
                    icon={<TrashcanAnimation className="edit-salary-info--result__icon" />}
                    successButton={frontPageButton()}
                />
            </div>
        );
    }

    if (showDeletionDialog) {
        return (
            <Content className="edit-salary-info--delete-dialog">
                <Icon className="edit-salary-info--delete-dialog__icon" type={IconTypes.UTROPSTEGN_36} />
                <h2 className="typography-h3">{translated.salaryInfo.edit.confirmDelete}</h2>
                <div className="edit-salary-info--delete-dialog__btn-container">
                    <LinkButton onClick={() => setShowDeletionDialog(false)}>{translated.cancel}</LinkButton>
                    <StandardButton onClick={deleteSalaryInfo}>{translated.confirmDelete}</StandardButton>
                </div>
            </Content>
        );
    }

    return (
        <Content>
            <form onSubmit={onSubmit} className="edit-salary-info__form">
                <div className="edit-salary-info__select-list">
                    <SelectList>
                        <li>
                            {isLoading ? (
                                <FieldSkeleton isNoteField />
                            ) : (
                                <SalaryDateField paydays={paydays!} setPaydays={setPaydays} error={paydaysError} />
                            )}
                        </li>
                        <li>
                            {isLoading ? (
                                <FieldSkeleton />
                            ) : (
                                <SalaryAccountField
                                    selectedAccount={selectedAccount}
                                    setSelectedAccount={setSelectedAccount}
                                    error={accountError}
                                />
                            )}
                        </li>
                    </SelectList>
                </div>
                <p className="edit-salary-info__small-text">{translated.salaryInfo.edit.description}</p>
                <p className="edit-salary-info__small-text">{translated.salaryInfo.edit.descriptionGdpr}</p>
                {!!salaryInfo && !hasChange && (
                    <Button
                        icon={IconTypes.SLETT_24}
                        buttonType="negative"
                        size="small"
                        onClick={() => setShowDeletionDialog(true)}
                        className="edit-salary-info__delete-btn"
                    >
                        {translated.delete}
                    </Button>
                )}
                {!salaryInfo ? (
                    <ButtonWithSpinner
                        buttonType="positive"
                        showSpinner={save.isPending}
                        type="submit"
                        className="edit-salary-info__save-btn"
                    >
                        {translated.save}
                    </ButtonWithSpinner>
                ) : (
                    <>
                        {hasChange && (
                            <ButtonWithSpinner
                                buttonType="positive"
                                showSpinner={save.isPending}
                                type="submit"
                                className="edit-salary-info__save-btn"
                            >
                                {translated.update}
                            </ButtonWithSpinner>
                        )}
                    </>
                )}
            </form>
        </Content>
    );
};

export default EditSalaryInfo;
